import { LiveAgentQueue } from '@bingads-webui-react/msa-copilot-common';
import {
  isCustomerSupportClosedForHoliday,
  LIVE_AGENT_OPTIMIZATION_QUEUE_HOLIDAY_CLOSE_HOURS_LOCALE_MAP,
  isEnglishOnlyChatSupportedLocale,
} from './live-agent-locale-utils';

export const defaultLiveAgentQueueList: LiveAgentQueue[] = [
  LiveAgentQueue.General,
  LiveAgentQueue.Optimization,
  LiveAgentQueue.Agency,
  LiveAgentQueue.ShoppingAndUET,
];

export const customLiveAgentQueueList: Record<string, LiveAgentQueue[]> = {
  'ja-JP': [LiveAgentQueue.General],
  'zh-CN': [LiveAgentQueue.General],
};

export interface LiveAgentQueueOption {
  queue: LiveAgentQueue;
  label: string;
  shouldShow?: (locale: string, isIntelligentRoutingEnabled?: boolean) => boolean;
}

export const isConsultationOptimizationQueueOpen = () => {
  // Get current time in PST
  const now = new Date();
  const pstNow = new Date(now.toLocaleString('en-US', { timeZone: 'America/Los_Angeles' }));

  // Create start time (Sunday 4 PM PST)
  const start = new Date(pstNow);
  start.setDate(pstNow.getDate() - pstNow.getDay()); // Set to Sunday
  start.setHours(16, 0, 0, 0); // Set to 4:00 PM on Sunday

  // Create end time (Friday 6 PM PST)
  const end = new Date(pstNow);
  end.setDate(pstNow.getDate() - pstNow.getDay() + 5); // Set to Friday
  end.setHours(18, 0, 0, 0); // Set to 6:00 PM on Friday

  // Check if current time is between the start and end time
  return pstNow >= start && pstNow <= end;
};

export const liveAgentQueueOptions: LiveAgentQueueOption[] = [
  {
    queue: LiveAgentQueue.General,
    label: _TL_('General questions'),
  },
  {
    queue: LiveAgentQueue.Optimization,
    label: _TL_('Consultation: Campaign optimization'),
    shouldShow: (locale: string, isIntelligentRoutingEnabled?: boolean): boolean => {
      if (isEnglishOnlyChatSupportedLocale(locale)) {
        return true;
      }
      // check if customer support is closed for holiday
      if (isCustomerSupportClosedForHoliday(locale, LIVE_AGENT_OPTIMIZATION_QUEUE_HOLIDAY_CLOSE_HOURS_LOCALE_MAP)) {
        return false;
      }
      const containsEn = locale?.toLowerCase()?.includes('en');
      return !!(isIntelligentRoutingEnabled && containsEn && isConsultationOptimizationQueueOpen());
    },
  },
  {
    queue: LiveAgentQueue.Agency,
    label: _TL_('Agency questions'),
  },
  {
    queue: LiveAgentQueue.ShoppingAndUET,
    label: _TL_('Shopping or conversion tracking'),
  },
];

export const getLiveAgentQueueList = (locale: string, isIntelligentRoutingEnabled?: boolean): LiveAgentQueueOption[] => {
  const options = customLiveAgentQueueList[locale] || defaultLiveAgentQueueList;

  return liveAgentQueueOptions.filter((option) => options.includes(option.queue)
    && (option.shouldShow ? option.shouldShow(locale, isIntelligentRoutingEnabled) : true));
};
